import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './OrganSystemCircle.css';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import OrganIcon from './OrganIcons';
import { getOrganSystemDescription } from '../../data/organSystemDescriptions';
import OrganSystemLinePlot from '../OrganSystemLinePlot/OrganSystemLinePlot';
import SegmentedControl from '../SegmentedControl/SegmentedControl';

/**
 * Trend arrow component - blocky arrow indicating score improvement/decline
 */
const TrendArrow = ({ direction, onClick, systemName }) => {
  if (!direction) return null;

  const isUp = direction === 'up';
  const label = isUp ? `${systemName} improved` : `${systemName} declined`;
  const color = isUp ? '#22c55e' : '#f97316'; // green for up, orange for down

  // Blocky arrow path (house shape)
  const arrowPath = isUp
    ? 'M4 14 L4 7 L1 7 L8 1 L15 7 L12 7 L12 14 Z'   // Up arrow
    : 'M4 2 L4 9 L1 9 L8 15 L15 9 L12 9 L12 2 Z';   // Down arrow

  return (
    <svg
      className="trendArrow"
      viewBox="0 0 16 16"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      role="button"
      tabIndex={0}
      aria-label={label}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onClick(); } }}
    >
      <path d={arrowPath} fill={color} />
    </svg>
  );
};

/**
 * Circular progress indicator for organ system health
 * Inner ring: health score (colored)
 * Outer ring: confidence (gray, thinner)
 */
function OrganSystemCircle({
  systemId,
  displayName,
  icon,
  score,
  confidence,
  color,
  selected = false,
  highlighted = false,
  focused = false,
  blurred = false,
  onTestPillClick,
  onClick,
  previousScore,
  onMoreInfoClick,
  onProtocolClick,
  onTimelineClick,
  bloodTestFiles,
  getFxRange,
  sex,
  selectedFileIndex
}) {
  const [isAnimated, setIsAnimated] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);

  // Trigger ring animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsAnimated(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Reset timeline view when modal closes
  useEffect(() => {
    if (!focused) setShowTimeline(false);
  }, [focused]);

  useBodyScrollLock(focused);

  // SVG dimensions
  const size = 90;
  const center = size / 2;
  const outerRadius = 40;
  const innerRadius = 32;
  const outerStrokeWidth = 4;  // Thinner outer ring (~40% of inner)
  const innerStrokeWidth = 10;

  // Calculate stroke dash for progress
  const outerCircumference = 2 * Math.PI * outerRadius;
  const innerCircumference = 2 * Math.PI * innerRadius;

  const confidencePercent = confidence?.percentage || 0;
  const scorePercent = score != null ? score : 0;

  const outerDashOffset = outerCircumference - (outerCircumference * confidencePercent / 100);
  const innerDashOffset = innerCircumference - (innerCircumference * scorePercent / 100);

  const isLowConfidence = confidence?.isLowConfidence;

  // Calculate trend direction for arrow display
  const trendDirection = useMemo(() => {
    if (previousScore == null || score == null) return null;
    const diff = score - previousScore;
    if (Math.abs(diff) < 5) return null;
    return diff > 0 ? 'up' : 'down';  // Higher score = better
  }, [score, previousScore]);

  // Round confidence to nearest 5%
  const roundedConfidence = Math.round((confidence?.percentage || 0) / 5) * 5;

  const classNames = [
    'organSystemCircle',
    selected ? 'selected' : '',
    highlighted ? 'highlighted' : '',
    isLowConfidence ? 'lowConfidence' : '',
    blurred ? 'blurred' : ''
  ].filter(Boolean).join(' ');

  return (
    <>
      <div className={classNames} onClick={onClick}>
        {renderCircleContent()}
      </div>

      {/* Centered focus modal - rendered to body via portal */}
      {focused && createPortal(
        <div
          className="focusModalOverlay"
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            if (e.target.classList.contains('focusModalOverlay')) {
              onClick();
            }
          }}
        >
          <div className="focusModalCard" onClick={(e) => e.stopPropagation()}>
            {/* Segmented control for Most Recent / Timeline */}
            {bloodTestFiles && bloodTestFiles.length > 1 && (
              <SegmentedControl
                segments={[
                  { id: 'recent', label: 'Most Recent', icon: (
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="2.5" strokeDasharray="33 11" strokeLinecap="round" transform="rotate(-90 10 10)" />
                    </svg>
                  )},
                  { id: 'timeline', label: 'Timeline', icon: (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                  )},
                ]}
                activeId={showTimeline ? 'timeline' : 'recent'}
                onSelect={(id) => setShowTimeline(id === 'timeline')}
              />
            )}

            {/* Conditional content: Timeline or Circle */}
            {showTimeline ? (
              <div className="focusTimelineContainer">
                <OrganSystemLinePlot
                  bloodTestFiles={bloodTestFiles}
                  getFxRange={getFxRange}
                  sex={sex}
                  selectedSystem={systemId}
                  onSystemClick={() => {}}
                  selectedFileIndex={selectedFileIndex}
                  hideLegend={true}
                />
              </div>
            ) : (
              <>
              {/* Row 1: Circle left, quick status right */}
              <div className="focusModalTop">
                <div className="focusCircle">
                  <div className="circleContainer">
                    <svg width={size} height={size} className="progressRings">
                      <circle className="outerRingBg" cx={center} cy={center} r={outerRadius} strokeWidth={outerStrokeWidth} />
                      <circle className="innerRingBg" cx={center} cy={center} r={innerRadius} strokeWidth={innerStrokeWidth} />
                      <circle className="outerRing" cx={center} cy={center} r={outerRadius} strokeWidth={outerStrokeWidth}
                        strokeDasharray={outerCircumference} strokeDashoffset={isAnimated ? outerDashOffset : outerCircumference} />
                      <circle className="innerRing" cx={center} cy={center} r={innerRadius} strokeWidth={innerStrokeWidth}
                        strokeDasharray={innerCircumference}
                        strokeDashoffset={isAnimated ? (isLowConfidence ? innerCircumference : innerDashOffset) : innerCircumference}
                        style={{ stroke: isLowConfidence ? '#9ca3af' : color }} />
                    </svg>
                    <div className="centerContent">
                      <div className="systemIcon"><OrganIcon icon={icon} /></div>
                      <span className="scoreValue" style={{ color: isLowConfidence ? '#9ca3af' : color }}>
                        {isLowConfidence ? 'N/A' : score}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="focusInfo">
                  <div className="focusQuickStatus">
                    Your {displayName}{' '}
                    {isLowConfidence || roundedConfidence < 60 ? (
                      <><strong>is inconclusive</strong>.{' '}
                        <button className="see-more-link" onClick={(e) => e.stopPropagation()}>
                          ...Get more tests
                        </button>
                      </>
                    ) : score >= 85 ? (
                      <><strong style={{ color }}>is strong</strong>.</>
                    ) : (
                      <><strong style={{ color }}>can use work</strong>.{' '}
                        {onProtocolClick ? (
                          <button className="see-more-link" onClick={(e) => {
                            e.stopPropagation();
                            onClick();
                            onProtocolClick();
                          }}>
                            ...Let's get started
                          </button>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 2: Confidence (left) | More Tests (right, scrollable) */}
              <div className="focusMiddleRow">
                {confidence && (
                  <div className="focusConfidenceText">
                    <strong>{roundedConfidence}%</strong> confidence.
                    {roundedConfidence < 50
                      ? ' More tests are needed to determine a score.'
                      : roundedConfidence < 80
                      ? ' Additional tests will strengthen this rating.'
                      : ' This rating is well supported by your tests.'}
                  </div>
                )}

                {confidence?.missingMarkers && (() => {
                  const { major = [], minor = [] } = confidence.missingMarkers;
                  if (major.length === 0 && minor.length === 0) return null;
                  return (
                    <div className="focusMoreTests">
                      <span className="indicatorsLabel">More Tests</span>
                      <div className="focusMoreTestsScroll">
                        {major.length > 0 && (
                          <div className="missingSection">
                            <span className="missingLabel">Strong</span>
                            <div className="missingList">
                              {major.map((m, i) => <span key={`maj-${i}`} className="missingMarkerTag major">{m}</span>)}
                            </div>
                          </div>
                        )}
                        {minor.length > 0 && (
                          <div className="missingSection">
                            <span className="missingLabel">Weak</span>
                            <div className="missingList">
                              {minor.map((m, i) => <span key={`min-${i}`} className="missingMarkerTag minor">{m}</span>)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Description */}
              {(() => {
                const systemInfo = getOrganSystemDescription(systemId);
                return systemInfo ? (
                  <div className="focusDescription">
                    {systemInfo.summary}{' '}
                    {onMoreInfoClick && (
                      <button className="see-more-link" onClick={(e) => {
                        e.stopPropagation();
                        onMoreInfoClick();
                      }}>
                        ...More info
                      </button>
                    )}
                  </div>
                ) : null;
              })()}
              </>
            )}

            {/* View Tests button below description */}
            {onTestPillClick && (
              <button className="actionPill viewTestsBottom" onClick={(e) => {
                e.stopPropagation();
                onClick(); // Close modal
                onTestPillClick(systemId); // Navigate to tests
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                  <rect x="9" y="3" width="6" height="4" rx="1" />
                </svg>
                View Tests
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );

  function renderCircleContent() {
    return (
      <>
        <div className="circleContainer">
          <svg width={size} height={size} className="progressRings">
            <circle className="outerRingBg" cx={center} cy={center} r={outerRadius} strokeWidth={outerStrokeWidth} />
            <circle className="innerRingBg" cx={center} cy={center} r={innerRadius} strokeWidth={innerStrokeWidth} />
            <circle className="outerRing" cx={center} cy={center} r={outerRadius} strokeWidth={outerStrokeWidth}
              strokeDasharray={outerCircumference} strokeDashoffset={isAnimated ? outerDashOffset : outerCircumference} />
            <circle className="innerRing" cx={center} cy={center} r={innerRadius} strokeWidth={innerStrokeWidth}
              strokeDasharray={innerCircumference}
              strokeDashoffset={isAnimated ? (isLowConfidence ? innerCircumference : innerDashOffset) : innerCircumference}
              style={{ stroke: isLowConfidence ? '#9ca3af' : color }} />
          </svg>

          <div className="centerContent">
            <div className="systemIcon"><OrganIcon icon={icon} /></div>
            <span className="scoreValue" style={{ color: isLowConfidence ? '#9ca3af' : color }}>
              {isLowConfidence ? 'N/A' : score}
            </span>
          </div>

          {bloodTestFiles && bloodTestFiles.length > 1 && (() => {
            const handleTimelineClick = onTimelineClick
              ? () => onTimelineClick()
              : () => { setShowTimeline(true); onClick(); };
            return trendDirection ? (
              <TrendArrow direction={trendDirection} onClick={handleTimelineClick} systemName={displayName} />
            ) : (
              <svg className="trendArrow timelineBtn" viewBox="0 0 16 16"
                onClick={(e) => { e.stopPropagation(); handleTimelineClick(); }}
                role="button" tabIndex={0} aria-label={`${displayName} timeline`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); handleTimelineClick(); } }}>
                <polyline points="14 8 12 8 10.5 14 5.5 2 4 8 2 8" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            );
          })()}
        </div>
        <div className="systemName">{displayName}</div>
      </>
    );
  }
}

export default OrganSystemCircle;
